import {
  Active,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, {forwardRef, useCallback, useMemo, useRef, useState} from 'react';
import {Components, Virtuoso, VirtuosoHandle} from 'react-virtuoso';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {AddSectionButton} from './AddSectionButton';
import {EmptyPanelBankMessage, EmptyPanelSearchMessage} from './EmptyMessages';
import {PanelCrossSectionDropIndicator} from './PanelCrossSectionDropIndicator';
import {SortablePanelBankSection} from './SortablePanelBankSection';
import {PanelDropRef, SectionWithRef} from './types';
import {useHandleDragEnd} from './useHandleDragEnd';
import {useHandleDragOver} from './useHandleDragOver';
import {useWorkspaceSections} from './useWorkspaceSections';
import {getDndSortableSectionId} from './utils';
import {WorkspaceSectionContextProvider} from './WorkspaceSectionContextProvider';

type VirtuosoContext = {
  isDraggingSection: boolean;
  isSearching: boolean;
  numVisibleSections: number;
};

const virtuosoComponents: Components<SectionWithRef, VirtuosoContext> = {
  Footer: forwardRef<HTMLDivElement, {context?: VirtuosoContext}>(
    ({context}, ref) => {
      const {numVisibleSections, isDraggingSection} = context!;

      if (!numVisibleSections) {
        return null;
      }

      return (
        <div
          ref={ref}
          style={{
            padding: '1rem 2rem',
            display: isDraggingSection ? 'none' : 'flex',
            justifyContent: 'flex-end',
          }}>
          <AddSectionButton />
        </div>
      );
    }
  ),
  EmptyPlaceholder: forwardRef<HTMLDivElement, {context?: VirtuosoContext}>(
    ({context}, ref) => {
      const {isSearching} = context!;

      return (
        <div ref={ref} style={{height: '100%'}}>
          {isSearching ? (
            <EmptyPanelSearchMessage />
          ) : (
            <EmptyPanelBankMessage />
          )}
        </div>
      );
    }
  ),
};

/**
 * This component is currently being built in parallel to the existing PanelBankSections component. This is currently behind a statsig gate.
 * https://console.statsig.com/2qsBpsviQSxHWKIyMZHZzm/gates?Tag=2TAfQEBRDyRI5yekkM1kA
 */
export const PanelBankSections = ({
  isProjectReadOnly,
}: {
  isProjectReadOnly: boolean;
}) => {
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  // TODO - add back useScrollToPanelBankSectionId

  // TODO - add data dog logging

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // This determines what sections should be displayed in the workspace
  // with search and filters applied. It is important to filter out sections
  // that are empty or should not be visible, so we can take advantages of
  // virtualization. https://virtuoso.dev/troubleshooting/#i-get-error-zero-sized-element-this-should-not-happen
  const visibleSections = useWorkspaceSections();

  const items = useMemo(() => {
    return visibleSections.map(s => getDndSortableSectionId(s.ref));
  }, [visibleSections]);

  const [activeItem, setActiveItem] = useState<Active | null>(null);
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveItem(event.active);
  }, []);

  // We intentionally use useRef here to avoid re-rendering
  // panelDropIndicatorRef keeps track of the styles needed to render the vertical bar
  // when a panel is dragged over a panel in a different section
  const panelDropIndicatorRef = useRef<HTMLDivElement>(null);
  // panelDropRef keeps track of the over data when a panel is dragged over a panel in a
  // different section. we need granular control to insert properly
  const panelDropRef = useRef<PanelDropRef | null>(null);

  // Make sure this is throttled.
  // This is used to determine if a panel is being dragged over a panel and the drop indicator
  // vertical bar should be visible
  const handleDragOver = useHandleDragOver(panelDropIndicatorRef, panelDropRef);

  const onHandleDragEnd = useHandleDragEnd(panelDropRef);
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // Order matters here - we should set activeItem to null first for a smooth drop experience
      setActiveItem(null);
      // Hide the panel cross section drop indicator
      if (panelDropIndicatorRef.current) {
        panelDropIndicatorRef.current.style.opacity = '0';
      }

      // We handle panel reordering within same section and panel moving to a different section
      onHandleDragEnd(event);
    },
    [onHandleDragEnd, panelDropIndicatorRef]
  );

  const {debouncedSearchRegex} = usePanelSearchDebouncedContext();
  const virtuosoContext: VirtuosoContext = useMemo(() => {
    return {
      isDraggingSection: activeItem !== null,
      isSearching: !!debouncedSearchRegex,
      numVisibleSections: visibleSections.length,
    };
  }, [activeItem, debouncedSearchRegex, visibleSections.length]);

  return (
    <DndContext
      sensors={sensors}
      // We use the default  "closest corners" collision detection since it's more aligned with
      // what the human eye would predict since the drag handle is in the top left corner.
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}>
      {/*
       * We want to render the panel cross section drop indicator and hide it when it's not needed.
       * This is to prevent unnecessary re-renders and optimize for performance.
       */}
      <PanelCrossSectionDropIndicator ref={panelDropIndicatorRef} />
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <Virtuoso
          components={virtuosoComponents}
          context={virtuosoContext}
          computeItemKey={idx =>
            getDndSortableSectionId(visibleSections[idx].ref)
          }
          data={visibleSections}
          itemContent={(idx, sectionWithRef) => {
            return (
              <WorkspaceSectionContextProvider
                sectionName={sectionWithRef.name}>
                <SectionSettingsContextProvider sectionRef={sectionWithRef.ref}>
                  <SortablePanelBankSection
                    idx={idx}
                    isProjectReadOnly={isProjectReadOnly}
                    numSections={visibleSections.length}
                    sectionWithRef={sectionWithRef}
                  />
                </SectionSettingsContextProvider>
              </WorkspaceSectionContextProvider>
            );
          }}
          ref={virtuosoRef}
          style={{flexGrow: 1}}
          useWindowScroll
        />
      </SortableContext>
      {/*
       * The <DragOverlay> component should remain mounted at all times so that it can perform the drop animation.
       * If you conditionally render the <DragOverlay> component, drop animations will not work.
       * Setting dropAnimation to null for a smoother drop experience
       * https://docs.dndkit.com/api-documentation/draggable/drag-overlay#drop-animation
       */}
      <DragOverlay dropAnimation={null}>
        {activeItem && (
          <div className="w-[5rem] cursor-pointer bg-white">
            <p>Temporary drag content</p>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
